import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Icon, Message } from 'semantic-ui-react';
import { API } from '../helpers';
import { creditErrorV108 } from '../pages/Organization/MemberCreditV108';
import '../pages/Organization/MemberCreditV108.css';

const RedemptionSettingV108 = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const load = useCallback(async () => {
    setLoadError('');
    try {
      const response = await API.get('/api/user/credit');
      if (!response?.data?.success)
        throw new Error(
          response?.data?.message || t('account_credit.load_error')
        );
      if (mounted.current) setSummary(response.data.data);
    } catch (e) {
      if (mounted.current)
        setLoadError(creditErrorV108(e, t('account_credit.load_error')));
    }
  }, [t]);
  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);
  const submit = async () => {
    if (busy.current || !code.trim()) return;
    busy.current = true;
    setSaving(true);
    setError('');
    setReceipt(null);
    try {
      const response = await API.post('/api/user/topup', { key: code.trim() });
      if (!response?.data?.success || !response.data.receipt)
        throw new Error(response?.data?.message || t('account_credit.failed'));
      if (mounted.current) {
        setReceipt(response.data.receipt);
        setCode('');
        await load();
      }
    } catch (e) {
      if (mounted.current)
        setError(creditErrorV108(e, t('account_credit.failed')));
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const destination = summary?.destination;
  const balance = summary?.available_usd;
  return (
    <section className='redemption-setting-v108'>
      <dl className='account-credit-summary-v108'>
        <div>
          <dt>{t('account_credit.scope')}</dt>
          <dd>
            {destination ? t(`account_credit.destination.${destination}`) : '-'}
          </dd>
        </div>
        <div>
          <dt>{t('account_credit.balance')}</dt>
          <dd>
            {!summary || loadError
              ? '-'
              : balance == null
              ? t('organization.unlimited')
              : `$${balance}`}
          </dd>
        </div>
        {summary?.parent_balance_usd != null && (
          <div>
            <dt>{t('account_credit.parent_balance')}</dt>
            <dd>${summary.parent_balance_usd}</dd>
          </div>
        )}
        {summary?.ledger_balance_usd != null && (
          <div>
            <dt>{t('account_credit.ledger_balance')}</dt>
            <dd>${summary.ledger_balance_usd}</dd>
          </div>
        )}
        {summary?.scope === 'member' && (
          <div>
            <dt>{t('account_credit.cycle_limit')}</dt>
            <dd>
              {summary.limit_usd == null
                ? t('organization.unlimited')
                : `$${summary.limit_usd}`}
            </dd>
          </div>
        )}
      </dl>
      {loadError && (
        <Message visible warning role='alert'>
          {loadError}
          <Button
            icon='refresh'
            aria-label={t('account_credit.retry')}
            onClick={load}
          />
        </Message>
      )}
      <Form onSubmit={submit}>
        <Form.Input
          id='redemption-code-v108'
          label={t('account_credit.code')}
          autoComplete='off'
          spellCheck={false}
          maxLength={128}
          value={code}
          disabled={saving}
          onChange={(_, data) => setCode(data.value)}
        />
        {error && <Message visible negative role='alert' content={error} />}
        {receipt && (
          <Message visible positive role='status'>
            <strong>
              {t('account_credit.redeemed')} +${receipt.amount_usd}
            </strong>
            <p>{t(`account_credit.destination.${receipt.destination}`)}</p>
          </Message>
        )}
        <Button
          primary
          type='submit'
          disabled={saving || !code.trim()}
          loading={saving}
        >
          <Icon name='ticket' />
          {t('account_credit.redeem')}
        </Button>
      </Form>
      {summary?.history?.length > 0 && (
        <section className='account-credit-history-v108'>
          <h3>{t('account_credit.history')}</h3>
          <ul>
            {summary.history.map((row) => (
              <li key={row.id}>
                <span>
                  {row.direction === 'debit' ? '-' : '+'}${row.amount_usd}
                </span>
                <span>{row.description}</span>
                <time dateTime={new Date(row.created_at * 1000).toISOString()}>
                  {new Date(row.created_at * 1000).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
};
export default RedemptionSettingV108;
